import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, User, BookOpen, XCircle, CheckCircle, Users, Filter, X, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate, formatTime12Hour, formatTimeRange } from '@/utils/formatters';

const RecentBookingsSection = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  
  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  
  // NEW: Booking source filter
  const [bookedByFilter, setBookedByFilter] = useState('ALL');

  useEffect(() => {
    // Fetch last 3 months of data by default
    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    
    const endDateStr = today.toISOString().split('T')[0];
    const startDateStr = threeMonthsAgo.toISOString().split('T')[0];
    
    fetchRecentBookings(startDateStr, endDateStr);
  }, []);

  const fetchRecentBookings = async (start = null, end = null, bookedBy = 'ALL') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build query params
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/recent-bookings?limit=50`;
      if (start && end) {
        url += `&start_date=${start}&end_date=${end}`;
      }
      // NEW: Add booked_by filter
      if (bookedBy && bookedBy !== 'ALL') {
        url += `&booked_by=${bookedBy}`;
      }
      
      const response = await axios.get(url, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch recent bookings:', error);
      toast.error('Failed to load recent bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    // Validate date range (max 3 months = 90 days)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 90) {
      toast.error('Date range cannot exceed 3 months (90 days)');
      return;
    }

    if (start > end) {
      toast.error('Start date must be before end date');
      return;
    }

    setFilterActive(true);
    fetchRecentBookings(startDate, endDate, bookedByFilter);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setBookedByFilter('ALL');
    setFilterActive(false);
    fetchRecentBookings();
  };
  
  // NEW: Handle booking source filter change
  const handleBookedByChange = (value) => {
    setBookedByFilter(value);
    if (filterActive) {
      fetchRecentBookings(startDate, endDate, value);
    } else {
      fetchRecentBookings(null, null, value);
    }
  };
  
  // NEW: Cancel booking function
  const handleCancelBooking = async (booking) => {
    if (booking.status === 'CANCELLED' || booking.action === 'CANCELLED') {
      toast.error('This booking is already cancelled');
      return;
    }
    
    // Confirm cancellation
    if (!window.confirm(`Are you sure you want to cancel this booking for ${booking.student_name}?`)) {
      return;
    }
    
    try {
      setCancellingBooking(booking.booking_id);
      const token = localStorage.getItem('token');
      const bookingType = booking.type; // 'mock' or 'partial'
      
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/booking/cancel/${bookingType}/${booking.booking_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Booking cancelled successfully');
      
      // Refresh bookings list
      if (filterActive) {
        fetchRecentBookings(startDate, endDate, bookedByFilter);
      } else {
        fetchRecentBookings(null, null, bookedByFilter);
      }
      
      // Close details modal if open
      if (detailsOpen && selectedBooking?.booking_id === booking.booking_id) {
        setDetailsOpen(false);
      }
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancellingBooking(null);
    }
  };

  const handleRowClick = (booking) => {
    setSelectedBooking(booking);
    setDetailsOpen(true);
  };

  const formatDateTime = (dateStr, timeStr) => {
    return {
      date: formatDate(dateStr),
      time: timeStr ? formatTime12Hour(timeStr) : 'N/A'
    };
  };

  const getActionBadge = (action) => {
    if (action === 'BOOKED') {
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle className="w-3 h-3 mr-1" />
          BOOKED
        </Badge>
      );
    } else if (action === 'CANCELLED') {
      return (
        <Badge variant="destructive">
          <XCircle className="w-3 h-3 mr-1" />
          CANCELLED
        </Badge>
      );
    }
    return <Badge>{action}</Badge>;
  };

  if (loading) {
    return (
      <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0 mt-8">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading bookings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0 mt-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                Recent Bookings & Cancellations
              </CardTitle>
              <CardDescription>Live updates from student booking activities</CardDescription>
            </div>
            <Badge variant="outline" className="text-sm">
              {bookings.length} activities
            </Badge>
          </div>
          
          {/* Date Range & Booking Source Filter */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900">Filter Bookings</span>
              </div>
              <span className="text-xs text-gray-500 italic">Max 3 months range</span>
            </div>
            
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <Label htmlFor="start-date" className="text-xs text-gray-600">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              <div className="flex-1 min-w-[180px]">
                <Label htmlFor="end-date" className="text-xs text-gray-600">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              
              {/* NEW: Booked By Filter */}
              <div className="flex-1 min-w-[180px]">
                <Label htmlFor="booked-by" className="text-xs text-gray-600">Booked By</Label>
                <Select value={bookedByFilter} onValueChange={handleBookedByChange}>
                  <SelectTrigger id="booked-by" className="mt-1">
                    <SelectValue placeholder="All Bookings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Bookings</SelectItem>
                    <SelectItem value="STUDENT">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>Booked by Student</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="INSTITUTE">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>Booked by Institute</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleApplyFilter}
                  disabled={!startDate || !endDate}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Apply
                </Button>
                
                {(filterActive || bookedByFilter !== 'ALL') && (
                  <Button
                    onClick={handleClearFilter}
                    variant="outline"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
            
            {(filterActive || bookedByFilter !== 'ALL') && (
              <div className="mt-3 text-xs text-blue-700 flex flex-wrap gap-2">
                {filterActive && (
                  <span>
                    <span className="font-semibold">Date:</span> {formatDate(startDate)} to {formatDate(endDate)}
                  </span>
                )}
                {bookedByFilter !== 'ALL' && (
                  <span>
                    <span className="font-semibold">•</span> <span className="font-semibold">Source:</span> {bookedByFilter === 'STUDENT' ? 'Booked by Student' : 'Booked by Institute'}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No recent bookings found
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map((booking, idx) => {
                const { date, time } = formatDateTime(booking.exam_date, booking.exam_time);
                const isInstituteBooking = booking.booked_by === 'INSTITUTE';
                
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-purple-50 rounded-lg transition-all hover:shadow-md border border-gray-100"
                  >
                    <div 
                      className="flex items-center gap-4 flex-1 cursor-pointer"
                      onClick={() => handleRowClick(booking)}
                    >
                      <div className="flex-shrink-0">
                        {getActionBadge(booking.action)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {isInstituteBooking ? (
                            <Building2 className="w-4 h-4 text-purple-500" />
                          ) : (
                            <User className="w-4 h-4 text-gray-500" />
                          )}
                          <span className="font-semibold text-gray-900">{booking.student_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {booking.batch}
                          </Badge>
                          {/* NEW: Booked By Indicator */}
                          {isInstituteBooking && (
                            <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                              <Building2 className="w-3 h-3 mr-1" />
                              Manual
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">{booking.module}</span>
                          {' • '}
                          <Calendar className="inline w-3 h-3" /> {date}
                          {' • '}
                          <Clock className="inline w-3 h-3" /> {time}
                          {booking.type === 'mock' && booking.speak_mode === 'ONLINE' && booking.whatsapp_number && (
                            <>
                              {' • '}
                              <span className="font-semibold text-green-700">📱 Online ({booking.whatsapp_number})</span>
                            </>
                          )}
                          {booking.candidate_no && booking.candidate_no !== 'N/A' && (
                            <>
                              {' • '}
                              <span className="text-xs">Candidate: {booking.candidate_no}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-xs text-gray-400">
                        {new Date(booking.timestamp).toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true,
                          timeZone: 'Asia/Dhaka'
                        })}
                      </div>
                      
                      {/* NEW: Cancel Button - Only show for MANUAL/INSTITUTE BOOKED bookings */}
                      {booking.action === 'BOOKED' && booking.booked_by === 'INSTITUTE' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelBooking(booking);
                          }}
                          disabled={cancellingBooking === booking.booking_id}
                          className="h-8 px-3"
                        >
                          {cancellingBooking === booking.booking_id ? (
                            'Cancelling...'
                          ) : (
                            <>
                              <XCircle className="w-3 h-3 mr-1" />
                              Cancel
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Booking Details
            </DialogTitle>
            <DialogDescription>
              Complete information about this booking activity
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                {getActionBadge(selectedBooking.action)}
              </div>
              
              {/* NEW: Booking Source Badge */}
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="text-sm text-gray-600">Booked By</span>
                {selectedBooking.booked_by === 'INSTITUTE' ? (
                  <Badge className="bg-purple-600">
                    <Building2 className="w-3 h-3 mr-1" />
                    Institute (Manual)
                  </Badge>
                ) : (
                  <Badge className="bg-blue-600">
                    <User className="w-3 h-3 mr-1" />
                    Student (Self)
                  </Badge>
                )}
              </div>
              
              <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  {selectedBooking.booked_by === 'INSTITUTE' ? (
                    <Building2 className="w-4 h-4 text-purple-500" />
                  ) : (
                    <User className="w-4 h-4 text-gray-500" />
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{selectedBooking.student_name}</p>
                    <p className="text-xs text-gray-500">{selectedBooking.student_email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Batch: <strong>{selectedBooking.batch}</strong></span>
                </div>
                
                {selectedBooking.candidate_no && selectedBooking.candidate_no !== 'N/A' && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm">Candidate No: <strong>{selectedBooking.candidate_no}</strong></span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2 bg-blue-50 p-4 rounded-lg">
                <div className="font-semibold text-blue-900 mb-2">
                  {selectedBooking.module}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span>{formatDateTime(selectedBooking.exam_date, selectedBooking.exam_time).date}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>{formatDateTime(selectedBooking.exam_date, selectedBooking.exam_time).time}</span>
                </div>
                
                {/* Speaking Details Display (for Mock exams) */}
                {selectedBooking.type === 'mock' && selectedBooking.speak_mode && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    {/* Speaking Time Slot */}
                    {selectedBooking.speaking_slot_time && (
                      <div className="flex items-center gap-2 mb-2 p-2 bg-purple-50 rounded">
                        <Clock className="w-4 h-4 text-purple-600" />
                        <div className="flex-1">
                          <span className="text-xs text-gray-600 block">Speaking Slot:</span>
                          <span className="text-sm font-semibold text-purple-700">
                            {selectedBooking.speaking_slot_time}
                          </span>
                          {selectedBooking.speaking_slot_date && (
                            <span className="text-xs text-gray-500 block mt-1">
                              {new Date(selectedBooking.speaking_slot_date).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Speaking Mode */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Speaking Mode:</span>
                      <Badge className={selectedBooking.speak_mode === 'ONLINE' ? 'bg-green-600' : 'bg-blue-600'}>
                        {selectedBooking.speak_mode === 'ONLINE' ? '📱 Online' : '🏢 Offline'}
                      </Badge>
                    </div>
                    
                    {/* WhatsApp Number */}
                    {selectedBooking.speak_mode === 'ONLINE' && selectedBooking.whatsapp_number && (
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-600">WhatsApp:</span>
                        <span className="text-xs font-semibold text-green-700">{selectedBooking.whatsapp_number}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="text-xs text-gray-500 text-center pt-2 border-t">
                Activity at: {new Date(selectedBooking.timestamp).toLocaleString('en-US', {
                  timeZone: 'Asia/Dhaka',
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })} (BD Time)
              </div>
              
              {/* NEW: Cancel button in modal - Only for MANUAL/INSTITUTE bookings */}
              {selectedBooking.action === 'BOOKED' && selectedBooking.booked_by === 'INSTITUTE' && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setDetailsOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleCancelBooking(selectedBooking)}
                    disabled={cancellingBooking === selectedBooking.booking_id}
                  >
                    {cancellingBooking === selectedBooking.booking_id ? (
                      'Cancelling...'
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel Booking
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {/* Info message for student bookings */}
              {selectedBooking.action === 'BOOKED' && selectedBooking.booked_by === 'STUDENT' && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mt-4">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Student Booking</p>
                    <p className="text-xs text-blue-700 mt-1">
                      To cancel this booking, please go to the student's profile page. Student will receive a cancellation email.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecentBookingsSection;
